import React, { useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import Button from '@atlaskit/button/new';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';

import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { expValEquals } from '@confluence/feature-experiments';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';

import { createTrelloCardHandle } from './createTrelloInboxHandlers.ts';
import { TrelloInboxIcon } from './TrelloInboxIcon.tsx';
import { useTrelloAccess } from './useTrelloAccess.ts';

const logger = getLogger('highlight-action');

type AddToTrelloInboxActionsProps = {
	activeSelection?: Selection | null;
	completeKey: string;
	contentId: string;
};

const getHighlightedText = (selection?: Selection | null): string => {
	if (!selection || selection.rangeCount === 0) {
		return '';
	}
	return selection.toString().trim();
};

const i18n = defineMessages({
	trelloLabel: {
		id: 'highlight-actions.create-trello-card',
		defaultMessage: 'Send to your Trello Inbox',
		description: 'Create a Trello card from highlighted text',
	},
	errorTitle: {
		id: 'highlight-actions.trello.create.error',
		defaultMessage: "Couldn't create Trello card. Try again later.",
		description:
			'The text is shown as the title of an error flag notification when creating a Trello card from highlighted page text fails, prompting the user to try again later.',
	},
	successTitle: {
		id: 'highlight-actions.trello.create.success',
		defaultMessage: 'Trello card created',
		description: 'Shown when Trello card creation succeeds',
	},
	openCard: {
		id: 'highlight-actions.trello.openCard',
		defaultMessage: 'Open card',
		description:
			'The text is shown as a link action in the success flag notification after a Trello card has been created from highlighted text. Clicking it opens the newly created Trello card in a new tab.',
	},
});

const AddToTrelloInboxActionsComponent = ({
	activeSelection,
	completeKey,
	contentId,
	flags,
}: AddToTrelloInboxActionsProps & WithFlagsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { hasTrelloAccess, loading: trelloAccessLoading } = useTrelloAccess();

	const fireTrelloCardCreatedAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'trelloInboxCard',
				type: 'ui',
				attributes: {
					contentId,
					hasSelection: Boolean(activeSelection && activeSelection.toString().trim()),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentId, activeSelection]);

	const showCreateErrorFlag = useCallback(() => {
		void flags.showFlag({
			id: 'trello-inbox-create-error',
			type: 'error',
			title: intl.formatMessage(i18n.errorTitle),
			close: 'auto',
		});
	}, [flags, intl]);

	const makeHandleCreateTrelloCardClick = useCallback(async () => {
		const highlightedText = getHighlightedText(activeSelection);

		if (!highlightedText) {
			return;
		}

		try {
			fireTrelloCardCreatedAnalytics();
			const result = await createTrelloCardHandle({
				text: highlightedText,
				contentId,
			});
			if (result && result.success) {
				const actions = result.cardUrl
					? [
							{
								content: intl.formatMessage(i18n.openCard),
								href: result.cardUrl,
								target: '_blank',
							} as const,
						]
					: undefined;
				void flags.showFlag({
					id: 'trello-inbox-create-success',
					type: 'success-circle',
					title: intl.formatMessage(i18n.successTitle),
					actions,
					close: 'auto',
				});
			} else {
				showCreateErrorFlag();
			}
		} catch {
			logger.error`Error creating Trello card for contentId=${contentId}`;
			showCreateErrorFlag();
		}
	}, [
		activeSelection,
		contentId,
		fireTrelloCardCreatedAnalytics,
		flags,
		showCreateErrorFlag,
		intl,
	]);

	if (!fg('quick_capture_trello_inbox')) {
		return null;
	}

	if (trelloAccessLoading) {
		return null;
	}

	if (!hasTrelloAccess) {
		return null;
	}

	if (
		fg('aifc_create_enabled') ||
		expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true)
	) {
		return (
			<ToolbarDropdownItem
				testId="create-trello-card-button"
				data-key={completeKey}
				shouldTitleWrap={false}
				elemBefore={<TrelloInboxIcon label={intl.formatMessage(i18n.trelloLabel)} size="small" />}
				onClick={makeHandleCreateTrelloCardClick}
			>
				{intl.formatMessage(i18n.trelloLabel)}
			</ToolbarDropdownItem>
		);
	}

	return editorExperiment('platform_editor_controls', 'variant1') ? (
		<DropdownItem
			testId="create-trello-card-button"
			aria-label={intl.formatMessage(i18n.trelloLabel)}
			data-key={completeKey}
			shouldTitleWrap={false}
			title={intl.formatMessage(i18n.trelloLabel)}
			elemBefore={<TrelloInboxIcon label={intl.formatMessage(i18n.trelloLabel)} />}
			onClick={makeHandleCreateTrelloCardClick}
		>
			{intl.formatMessage(i18n.trelloLabel)}
		</DropdownItem>
	) : (
		<Button
			id="create-trello-card-button"
			appearance="subtle"
			iconBefore={() => <TrelloInboxIcon label={intl.formatMessage(i18n.trelloLabel)} />}
			data-key={completeKey}
			testId="create-trello-card-button"
			onClick={makeHandleCreateTrelloCardClick}
			spacing="compact"
		>
			{intl.formatMessage(i18n.trelloLabel)}
		</Button>
	);
};

export const AddToTrelloInboxActions = withFlags(AddToTrelloInboxActionsComponent);
